import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Tabs,
  Tab,
  Grid,
  Paper,
  Stack,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationCity as LocationIcon,
  Work as WorkIcon,
  Description as DescriptionIcon,
  Star as StarIcon,
  Note as NoteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pause as PauseIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import publicApplicationsService from '../services/publicApplicationsService';
import EvaluationForm from './EvaluationForm';
import NotesTimeline from './NotesTimeline';
import CVViewerModal from './CVViewerModal';

const ApplicationDetailModal = ({ open, onClose, application, onApplicationUpdated }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [updating, setUpdating] = useState(false);
  const [cvViewerOpen, setCvViewerOpen] = useState(false);
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const [loadingProfilePic, setLoadingProfilePic] = useState(false);

  const statusLabels = {
    'pending': 'Pending',
    'under_review': 'Under Review',
    'shortlisted': 'Shortlisted',
    'rejected': 'Rejected',
    'on_hold': 'On Hold',
    'consider_other_role': 'Consider Other Role',
    'moved_to_candidate': 'Moved to Candidate',
  };

  const statusColors = {
    'pending': 'warning',
    'under_review': 'info',
    'shortlisted': 'success',
    'rejected': 'error',
    'on_hold': 'default',
    'consider_other_role': 'secondary',
    'moved_to_candidate': 'primary',
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleQuickAction = async (newStatus) => {
    setUpdating(true);
    try {
      await publicApplicationsService.updateStatus(application.id, newStatus);
      if (onApplicationUpdated) {
        onApplicationUpdated();
      }
      onClose();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleViewCV = () => {
    setCvViewerOpen(true);
  };

  const handleCloseViewer = () => {
    setCvViewerOpen(false);
  };

  const loadProfilePicture = async () => {
    if (application.profile_picture_path && selectedTab === 2) {
      setLoadingProfilePic(true);
      try {
        const response = await publicApplicationsService.getProfilePictureUrl(application.id);
        if (response.success) {
          setProfilePictureUrl(response.url);
        }
      } catch (error) {
        console.error('Error loading profile picture:', error);
      } finally {
        setLoadingProfilePic(false);
      }
    }
  };

  React.useEffect(() => {
    loadProfilePicture();
  }, [selectedTab, application.id]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const InfoRow = ({ icon, label, value }) => (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1.5 }}>
      <Box sx={{ color: '#29204C', display: 'flex', alignItems: 'center' }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {label}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {value || '-'}
        </Typography>
      </Box>
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, maxHeight: '90vh' }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ bgcolor: '#29204C', color: 'white', py: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ width: 48, height: 48, bgcolor: 'white', color: '#29204C' }}>
              {application.candidate?.full_name?.charAt(0) || 'N'}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {application.candidate?.full_name || 'N/A'}
              </Typography>
              <Chip
                label={statusLabels[application.status]}
                color={statusColors[application.status]}
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      {/* Quick Actions */}
      <Box sx={{ bgcolor: '#f5f5f5', px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            size="small"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={() => handleQuickAction('shortlisted')}
            disabled={updating || application.status === 'shortlisted'}
          >
            Shortlist
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            startIcon={<CancelIcon />}
            onClick={() => handleQuickAction('rejected')}
            disabled={updating || application.status === 'rejected'}
          >
            Reject
          </Button>
          <Button
            variant="contained"
            size="small"
            color="warning"
            startIcon={<PauseIcon />}
            onClick={() => handleQuickAction('on_hold')}
            disabled={updating || application.status === 'on_hold'}
          >
            On Hold
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DescriptionIcon />}
            onClick={handleViewCV}
          >
            View CV
          </Button>
        </Stack>
      </Box>

      {/* Tabs */}
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          px: 3,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
          '& .Mui-selected': { color: '#29204C' },
          '& .MuiTabs-indicator': { bgcolor: '#29204C' },
        }}
      >
        <Tab label="Personal Info" />
        <Tab label="Application Details" />
        <Tab label="Documents" />
        <Tab label="Professional Info" />
        <Tab label="Evaluation" />
        <Tab label="Notes" />
      </Tabs>

      {/* Content */}
      <DialogContent sx={{ p: 3 }}>
        {/* Tab 0: Personal Info */}
        {selectedTab === 0 && (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#29204C' }}>
                    Contact Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <InfoRow icon={<PersonIcon />} label="Full Name" value={application.candidate?.full_name} />
                  <InfoRow icon={<EmailIcon />} label="Email" value={application.candidate?.email} />
                  <InfoRow icon={<PhoneIcon />} label="Mobile" value={application.candidate?.mobile} />
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#29204C' }}>
                    Location Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <InfoRow icon={<LocationIcon />} label="City" value={application.candidate?.city} />
                  <InfoRow icon={<LocationIcon />} label="Address" value={application.candidate?.address} />
                  <InfoRow icon={<LocationIcon />} label="Nationality" value={application.candidate?.nationality} />
                  <InfoRow icon={<PersonIcon />} label="Marital Status" value={application.candidate?.marital_status} />
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Tab 1: Application Details */}
        {selectedTab === 1 && (
          <Box>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#29204C' }}>
                Application Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <InfoRow icon={<WorkIcon />} label="Position Applied For" value={application.job?.job_title || application.job?.posting_title || application.job_title || 'N/A'} />
              <InfoRow icon={<DescriptionIcon />} label="How They Heard" value={application.how_heard} />
              {application.how_heard_other && (
                <InfoRow icon={<DescriptionIcon />} label="Other Details" value={application.how_heard_other} />
              )}
              {application.referral_name && (
                <InfoRow icon={<PersonIcon />} label="Referral Name" value={application.referral_name} />
              )}
              <InfoRow icon={<DescriptionIcon />} label="Application Date" value={formatDate(application.created_at)} />

              {application.message && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Message
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                    <Typography variant="body1">{application.message}</Typography>
                  </Paper>
                </Box>
              )}
            </Paper>
          </Box>
        )}

        {/* Tab 2: Documents */}
        {selectedTab === 2 && (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                    Profile Picture
                  </Typography>
                  {application.profile_picture_path ? (
                    loadingProfilePic ? (
                      <CircularProgress />
                    ) : (
                      <Box
                        component="img"
                        src={profilePictureUrl || application.profile_picture_path}
                        alt="Profile"
                        sx={{ maxWidth: '100%', maxHeight: 400, borderRadius: 2 }}
                      />
                    )
                  ) : (
                    <Typography color="text.secondary">No profile picture uploaded</Typography>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                    CV Document
                  </Typography>
                  {application.cv_path ? (
                    <Box>
                      <DescriptionIcon sx={{ fontSize: 100, color: '#29204C', mb: 2 }} />
                      <Button
                        variant="contained"
                        startIcon={<DescriptionIcon />}
                        onClick={handleViewCV}
                        sx={{ bgcolor: '#29204C' }}
                      >
                        View CV
                      </Button>
                    </Box>
                  ) : (
                    <Typography color="text.secondary">No CV uploaded</Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Tab 3: Professional Info */}
        {selectedTab === 3 && (
          <Box>
            <Grid container spacing={3}>
              {/* Professional Links */}
              {(application.linkedin_url || application.portfolio_url || application.github_url) && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#29204C' }}>
                      Professional Links
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    {application.linkedin_url && (
                      <InfoRow icon={<LanguageIcon />} label="LinkedIn" value={application.linkedin_url} />
                    )}
                    {application.portfolio_url && (
                      <InfoRow icon={<LanguageIcon />} label="Portfolio" value={application.portfolio_url} />
                    )}
                    {application.github_url && (
                      <InfoRow icon={<LanguageIcon />} label="GitHub" value={application.github_url} />
                    )}
                  </Paper>
                </Grid>
              )}

              {/* Work Experience */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#29204C' }}>
                    Work Experience
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <InfoRow icon={<WorkIcon />} label="Years of Experience" value={application.years_of_experience} />
                  <InfoRow icon={<WorkIcon />} label="Current Job Title" value={application.current_job_title} />
                  <InfoRow icon={<WorkIcon />} label="Current Company" value={application.current_company} />
                  <InfoRow icon={<WorkIcon />} label="Notice Period" value={application.notice_period} />
                </Paper>
              </Grid>

              {/* Education */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#29204C' }}>
                    Education
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <InfoRow icon={<DescriptionIcon />} label="Education Level" value={application.education_level} />
                  <InfoRow icon={<DescriptionIcon />} label="Field of Study" value={application.field_of_study} />
                  <InfoRow icon={<DescriptionIcon />} label="University" value={application.university_name} />
                  <InfoRow icon={<DescriptionIcon />} label="Graduation Year" value={application.graduation_year} />
                </Paper>
              </Grid>

              {/* Salary Expectations */}
              {application.expected_salary && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#29204C' }}>
                      Salary Expectations
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <InfoRow icon={<WorkIcon />} label="Expected Salary" value={application.expected_salary} />
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* Tab 4: Evaluation */}
        {selectedTab === 4 && (
          <Box>
            {/* Existing Evaluations */}
            {application.evaluations && application.evaluations.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#29204C' }}>
                  Previous Evaluations
                </Typography>
                <Stack spacing={2}>
                  {application.evaluations.map((evaluation, index) => (
                    <Paper key={index} sx={{ p: 3, bgcolor: '#f5f5f5' }}>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Evaluated by: {evaluation.evaluator?.name || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(evaluation.created_at)}
                        </Typography>
                      </Stack>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        {evaluation.overall_rating && (
                          <Grid item xs={6} sm={4}>
                            <Typography variant="caption" color="text.secondary">Overall</Typography>
                            <Stack direction="row" spacing={0.5}>
                              {[...Array(5)].map((_, i) => (
                                <StarIcon
                                  key={i}
                                  fontSize="small"
                                  sx={{ color: i < evaluation.overall_rating ? '#ffa726' : '#e0e0e0' }}
                                />
                              ))}
                            </Stack>
                          </Grid>
                        )}
                        {evaluation.technical_rating && (
                          <Grid item xs={6} sm={4}>
                            <Typography variant="caption" color="text.secondary">Technical</Typography>
                            <Stack direction="row" spacing={0.5}>
                              {[...Array(5)].map((_, i) => (
                                <StarIcon
                                  key={i}
                                  fontSize="small"
                                  sx={{ color: i < evaluation.technical_rating ? '#ffa726' : '#e0e0e0' }}
                                />
                              ))}
                            </Stack>
                          </Grid>
                        )}
                        {evaluation.communication_rating && (
                          <Grid item xs={6} sm={4}>
                            <Typography variant="caption" color="text.secondary">Communication</Typography>
                            <Stack direction="row" spacing={0.5}>
                              {[...Array(5)].map((_, i) => (
                                <StarIcon
                                  key={i}
                                  fontSize="small"
                                  sx={{ color: i < evaluation.communication_rating ? '#ffa726' : '#e0e0e0' }}
                                />
                              ))}
                            </Stack>
                          </Grid>
                        )}
                      </Grid>
                      <Chip
                        label={evaluation.recommendation}
                        size="small"
                        color={
                          evaluation.recommendation === 'shortlist' ? 'success' :
                          evaluation.recommendation === 'reject' ? 'error' :
                          'default'
                        }
                        sx={{ mb: evaluation.evaluation_notes ? 2 : 0 }}
                      />
                      {evaluation.evaluation_notes && (
                        <Typography variant="body2" sx={{ mt: 2 }}>
                          {evaluation.evaluation_notes}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}

            {/* New Evaluation Form */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#29204C' }}>
                Add New Evaluation
              </Typography>
              <EvaluationForm
                applicationId={application.id}
                onSuccess={() => {
                  if (onApplicationUpdated) {
                    onApplicationUpdated();
                  }
                  onClose();
                }}
              />
            </Paper>
          </Box>
        )}

        {/* Tab 5: Notes */}
        {selectedTab === 5 && (
          <Box>
            <NotesTimeline
              applicationId={application.id}
              notes={application.notes || []}
              onNoteAdded={() => {
                if (onApplicationUpdated) {
                  onApplicationUpdated();
                }
              }}
            />
          </Box>
        )}
      </DialogContent>

      {/* CV Viewer Modal */}
      <CVViewerModal
        open={cvViewerOpen}
        onClose={handleCloseViewer}
        applicationId={application.id}
        applicantName={application.candidate?.full_name || 'N/A'}
      />
    </Dialog>
  );
};

export default ApplicationDetailModal;
